import { TOKEN_LIFETIME_MS } from '@/integrations/idb/models/users';
import { SESSION_CLEAR_DEADLINE_MS } from './refresh-session';

export const REFRESH_AHEAD_MS = TOKEN_LIFETIME_MS / 3;
export const MIN_DELAY_MS = 1_000;
const TRANSIENT_BACKOFF_BASE_MS = 2_000;
const MAX_TRANSIENT_BACKOFF_MS = 30_000;

type ScheduleParams = {
  tokenExpiredAt: Date;
  onRefresh: (scheduledFor: Date) => Promise<void>;
};

type DeadlineScheduleParams = {
  tokenExpiredAt: Date;
  onDeadline: () => Promise<void>;
};

let tokenRefreshTimer: number | null = null;
let clearDeadlineTimer: number | null = null;
let transientAttempts: { key: number; attempts: number } | null = null;

const clearScheduledRefresh = () => {
  if (tokenRefreshTimer !== null) {
    window.clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
};

const clearScheduledDeadline = () => {
  if (clearDeadlineTimer !== null) {
    window.clearTimeout(clearDeadlineTimer);
    clearDeadlineTimer = null;
  }
};

const schedule = (
  delay: number,
  { tokenExpiredAt, onRefresh }: ScheduleParams,
) => {
  clearScheduledRefresh();
  tokenRefreshTimer = window.setTimeout(() => {
    tokenRefreshTimer = null;
    void onRefresh(tokenExpiredAt).catch(console.error);
  }, delay);
};

export const scheduleTokenRefresh = ({
  tokenExpiredAt,
  onRefresh,
}: ScheduleParams): void => {
  const delay = Math.max(
    tokenExpiredAt.getTime() - Date.now() - REFRESH_AHEAD_MS,
    MIN_DELAY_MS,
  );

  schedule(delay, { tokenExpiredAt, onRefresh });
};

export const scheduleTransientRetry = (params: ScheduleParams): void => {
  const key = params.tokenExpiredAt.getTime();
  const attempts =
    transientAttempts?.key === key ? transientAttempts.attempts : 0;
  const delay = Math.min(
    TRANSIENT_BACKOFF_BASE_MS * 2 ** attempts,
    MAX_TRANSIENT_BACKOFF_MS,
  );

  transientAttempts = { key, attempts: attempts + 1 };
  schedule(delay, params);
};

export const resetTransientBackoff = (): void => {
  transientAttempts = null;
};

export const scheduleClearDeadline = ({
  tokenExpiredAt,
  onDeadline,
}: DeadlineScheduleParams): void => {
  clearScheduledDeadline();
  const delay = Math.max(
    tokenExpiredAt.getTime() + SESSION_CLEAR_DEADLINE_MS - Date.now(),
    MIN_DELAY_MS,
  );

  clearDeadlineTimer = window.setTimeout(() => {
    clearDeadlineTimer = null;
    void onDeadline().catch(console.error);
  }, delay);
};

export const cancelAllRefreshSchedules = (): void => {
  clearScheduledRefresh();
  clearScheduledDeadline();
};
